import  React, { useEffect, useState, useRef, useCallback } from "react";
import { useNavigate, useLocation } from "react-router-dom";
import { connectSocket } from "../socket";
import SideBar from "./SideBar";
import ImagePreviewModal from "./ImagePreviewModal";
import UserProfileMenu from "./UserProfileMenu";
import FeedbackModal from "./FeedbackModal";
import { DiagnosticMapWindow } from "./tse";
import { Zap } from "lucide-react";
import { authFetch } from "../utils/auth";

// ── Knowledge-graph cache endpoint ─────────────────────────────────────────
// The backend serves the full product graph ONCE per session via this endpoint.
// Chat responses no longer embed diagnostic_subgraph / session_snapshot.
const KNOWLEDGE_GRAPH_ENDPOINT = (product) => `/api/knowledge-graph/${encodeURIComponent(product)}`;

// Cache-hit badge. Blue and solid-filled so it reads as a bolt rather than as
// one more muted glyph — kept in sync with .d5-cache-indicator in dashboard5.css.
const CacheIndicator = () => (
  <span className="cache-indicator" title="Response served from cache">
    <Zap size={11} strokeWidth={2} />
  </span>
);

function downloadSessionMarkdown(dashboardMessages) {
  const lines = ["# Copilot Session", "", "---"];
  const skip = new Set(["rag_searching", "rag_results", "session_clear_divider"]);
  for (const msg of dashboardMessages) {
    if (skip.has(msg.type)) continue;
    const ts = msg.timestamp ? new Date(msg.timestamp).toLocaleString([], { month: "short", day: "numeric", hour: "2-digit", minute: "2-digit" }) : "";
    lines.push("");
    if (msg.type === "answered_question" && msg.questionSnapshot) {
      lines.push(`## 🤖 Copilot${ts ? ` — ${ts}` : ""}`);
      lines.push("", msg.questionSnapshot.question || "");
      if (msg.selectedAnswer) lines.push("", `**Your answer:** ${msg.selectedAnswer}`);
    } else {
      const label = msg.sender === "agent" ? "👤 You" : msg.sender === "user" ? "👤 Customer" : "🤖 Copilot";
      lines.push(`## ${label}${ts ? ` — ${ts}` : ""}`);
      lines.push("", msg.text || "");
    }
    lines.push("", "---");
  }
  lines.push("", "*Exported from Arta Copilot*");
  const blob = new Blob([lines.join("\n")], { type: "text/markdown;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url;
  a.download = `copilot-session-${new Date().toISOString().slice(0, 10)}.md`;
  document.body.appendChild(a);
  a.click();
  document.body.removeChild(a);
  URL.revokeObjectURL(url);
}

const isImagePreviewLink = (label = "", url = "") => {
  const normalizedLabel = label.trim().toLowerCase();
  const normalizedUrl = url.trim().toLowerCase();

  return (
    normalizedLabel.includes("image") ||
    normalizedLabel.includes("screenshot") ||
    /\.(png|jpe?g|gif|webp|bmp|svg)(\?|$)/i.test(normalizedUrl) ||
    normalizedUrl.includes("drive.google.com/file/d/")
  );
};

// ── Multi-select question widget (needs its own state) ──────────────────────
const MultiSelectWidget = ({ options, onSubmit, formatBotMessage }) => {
  const [selected, setSelected] = useState(new Set());
  const toggle = (opt) => {
    setSelected(prev => {
      const next = new Set(prev);
      if (next.has(opt)) next.delete(opt); else next.add(opt);
      return next;
    });
  };
  return (
    <div>
      <div className="flex flex-col gap-2">
        {options.map((opt, i) => (
          <button
            key={i}
            onClick={() => toggle(opt)}
            className={`text-left px-4 py-2.5 border rounded-lg text-sm transition flex items-center gap-2 ${
              selected.has(opt)
                ? "border-[#06b6d4] bg-cyan-50 text-cyan-800"
                : "border-gray-200 hover:border-[#06b6d4] hover:bg-cyan-50 text-[#253439]"
            }`}
          >
            <span className={`w-4 h-4 rounded border-2 flex items-center justify-center flex-shrink-0 ${
              selected.has(opt) ? "border-[#06b6d4] bg-[#06b6d4]" : "border-gray-300"
            }`}>
              {selected.has(opt) && (
                <svg width="10" height="10" fill="none" stroke="white" strokeWidth="3" viewBox="0 0 24 24">
                  <path d="M5 13l4 4L19 7" />
                </svg>
              )}
            </span>
            <span dangerouslySetInnerHTML={{ __html: formatBotMessage(opt) }} />
          </button>
        ))}
      </div>
      <button
        onClick={() => onSubmit(JSON.stringify([...selected]))}
        disabled={selected.size === 0}
        className="mt-3 px-5 py-2 bg-[#06b6d4] hover:bg-cyan-700 text-white rounded-lg text-sm font-medium transition disabled:opacity-40 disabled:cursor-not-allowed"
      >
        Submit ({selected.size} selected)
      </button>
    </div>
  );
};

// ── Text input question widget ──────────────────────────────────────────────
const TextQuestionWidget = ({ onSubmit }) => {
  const [value, setValue] = useState("");
  const handleKeyDown = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      if (value.trim()) onSubmit(value.trim());
    }
  };
  return (
    <div>
      <textarea
        className="w-full border border-gray-200 rounded-lg p-3 text-sm text-[#253439] placeholder-gray-400 focus:border-[#06b6d4] focus:ring-1 focus:ring-[#06b6d4] outline-none resize-none"
        style={{ minHeight: "60px", fontFamily: "'Space Grotesk', sans-serif" }}
        placeholder="Type your answer..."
        value={value}
        onChange={(e) => setValue(e.target.value)}
        onKeyDown={handleKeyDown}
        autoFocus
      />
      <button
        onClick={() => { if (value.trim()) onSubmit(value.trim()); }}
        disabled={!value.trim()}
        className="mt-2 px-5 py-2 bg-[#06b6d4] hover:bg-cyan-700 text-white rounded-lg text-sm font-medium transition disabled:opacity-40 disabled:cursor-not-allowed"
      >
        Submit
      </button>
    </div>
  );
};

const KNOWN_NODE_TYPES = new Set([
  "Product", "Problem", "Symptom", "Workflow", "Decision",
  "DiagnosticStep", "DiagnosticSteps", "PossibleCause",
  "Resolution", "Remediation", "Workaround", "Info",
]);

const inferTypeFromNid = (nid = "") => {
  const n = nid.toUpperCase();
  if (/^WF_/.test(n) || /^MAIN_/.test(n) || /_START/.test(n) || /^START_/.test(n)) return "Workflow";
  if (/^PC_/.test(n) || /_PC\d/.test(n) || /_PC_/.test(n) || /^CAUSE_/.test(n) || /POSSIBLE_CAUSE/.test(n)) return "PossibleCause";
  if (/^RES_/.test(n) || /_RES\d/.test(n) || /^RESOLUTION_/.test(n) || /^FIX_/.test(n)) return "Resolution";
  if (/^REM_/.test(n) || /^REMED_/.test(n) || /^RX_/.test(n)) return "Remediation";
  if (/^WORK_/.test(n) || /^WA_/.test(n) || /^WRK_/.test(n)) return "Workaround";
  if (/^DS_/.test(n) || /_DS\d/.test(n) || /^DIAG_/.test(n) || /^CHECK_/.test(n) || /^STEP_/.test(n)) return "DiagnosticStep";
  if (/^DEC_/.test(n) || /^Q_/.test(n) || /^Q\d+[A-Z]?$/.test(nid) || /_Q\d/.test(n)) return "Decision";
  if (/^PROB_/.test(n) || /^ROOT_/.test(n)) return "Problem";
  if (/^SYM_/.test(n) || /_SYM/.test(n) || /^S_/.test(n) || /^CC_/.test(n)) return "Symptom";
  return null;
};

const getDiagNodeFromCurrentNode = (node, fallback = {}) => {
  if (!node || typeof node !== "object") return null;
  const labels = node.labels || [];
  const id = node.nid || node.id || node.name || fallback.id;
  if (!id) return null;
  const type =
    node.type ||
    node.current_node_type ||
    labels.find((label) => KNOWN_NODE_TYPES.has(label)) ||
    inferTypeFromNid(id) ||
    fallback.type ||
    "Info";
  return {
    id,
    nid: node.nid || id,
    type,
    name: node.name || node.title || fallback.name || id,
    description: node.description || node.question || node.command || "",
    turn: fallback.turn,
  };
};

const TSECopilotDashboard = ({ onLogout, mode, username: loggedInUsername }) => {
  const navigate = useNavigate();
  const location = useLocation();
  const socketRef = useRef(null);
  const dashboardSendErrorTimerRef = useRef(null);
  const [dashboardSendError, setDashboardSendError] = useState("");
  const firstPromptRef = useRef(null); // first agent message of the session — for feedback metadata
  const [showFeedbackModal, setShowFeedbackModal] = useState(false);
  const [feedbackBlockedNotice, setFeedbackBlockedNotice] = useState("");
  const feedbackBlockedTimerRef = useRef(null);

  const openFeedbackModal = () => {
    if (!firstPromptRef.current || !socketRef.current?.id) {
      setFeedbackBlockedNotice("Ask a question first before sharing feedback.");
      if (feedbackBlockedTimerRef.current) window.clearTimeout(feedbackBlockedTimerRef.current);
      feedbackBlockedTimerRef.current = window.setTimeout(() => setFeedbackBlockedNotice(""), 4000);
      return;
    }
    setShowFeedbackModal(true);
  };
  
  // Separate message states for each chat box
  const [customerMessage, setCustomerMessage] = useState("");
  const [dashboardMessage, setDashboardMessage] = useState("");
  
  // Separate message lists for each chat box
  const [customerMessages, setCustomerMessages] = useState([]);
  const [dashboardMessages, setDashboardMessages] = useState([]);
  const [currentSession, setCurrentSession] = useState(null); // Store current session info
  const [isThinking, setIsThinking] = useState(false); // Thinking animation state
  const [isProcessing, setIsProcessing] = useState(false); // Processing state (after searches complete)
  const [waitingForAnswer, setWaitingForAnswer] = useState(false); // Disable input while question widget is active
  const [currentQuestion, setCurrentQuestion] = useState(null); // Track current question: { question, options, input_type, messageId }
  const [isQuestionCollapsed, setIsQuestionCollapsed] = useState(false); // Track if question section is collapsed
  const [implicitCases, setImplicitCases] = useState([]);
  const [implicitBugs, setImplicitBugs] = useState([]);
  const [hasNewCases, setHasNewCases] = useState(false);
  const [hasNewBugs, setHasNewBugs] = useState(false);
  const [showCasesDropdown, setShowCasesDropdown] = useState(false);
  const [showBugsDropdown, setShowBugsDropdown] = useState(false);
  const [loadingMoreCases, setLoadingMoreCases] = useState(false);
  const [loadingMoreBugs, setLoadingMoreBugs] = useState(false);
  const [diagMapTrail, setDiagMapTrail] = useState([]);
  const [diagMapSnapshot, setDiagMapSnapshot] = useState(null);       // cumulative — mini view
  const [diagMapFullSnapshot, setDiagMapFullSnapshot] = useState(null); // raw server — full screen
  const diagMapTurnRef = useRef(0);
  const [diagMapInlineOpen, setDiagMapInlineOpen] = useState(false);
  const [diagMapInlineMinimized, setDiagMapInlineMinimized] = useState(false);
  const [diagMapFullOpen, setDiagMapFullOpen] = useState(false);
  const [diagMapFullMinimized, setDiagMapFullMinimized] = useState(false);
  // Mini inline focus
  const [diagMapFocusNodeId, setDiagMapFocusNodeId] = useState(null);
  const [diagMapFocusKey, setDiagMapFocusKey] = useState(0);
  const [diagMapMaxTurn, setDiagMapMaxTurn] = useState(null);
  // Full screen focus — separate, not shared with mini
  const [diagMapFullFocusNodeId, setDiagMapFullFocusNodeId] = useState(null);
  const [diagMapFullFocusKey, setDiagMapFullFocusKey] = useState(0);
  const diagMapOpenRef = useRef(false);

  // ── Knowledge-graph cache ───────────────────────────────────────────────
  // fullGraphRef holds { nodes: [...], edges: [...] } fetched once per product.
  // graphFetchedForProductRef tracks which product is currently cached so we
  // refetch when the product changes within a session.
  // lastChatDataRef holds the most recent socket message so we can re-apply
  // the snapshot once the graph fetch resolves (timing: graph arrives after messages).
  const fullGraphRef = useRef(null);
  const graphFetchedForProductRef = useRef(null);
  const lastChatDataRef = useRef(null);
  const [graphLoading, setGraphLoading] = useState(false);

  // Usage stats for TSE mode
  const [, setUsageStats] = useState({
    requests: 0,
    total_input: 0,
    total_output: 0,
    total_cache_write: 0,
    total_cache_read: 0,
    total_cost_usd: 0,
    total_savings_usd: 0,
  });
  const [sessionStart, setSessionStart] = useState(null);
  
  const customerChatBodyRef = useRef(null);
  const dashboardChatBodyRef = useRef(null);
  const splitContainerRef = useRef(null);
  const isResizingRef = useRef(false);
  const clearDividerRef = useRef(null); // Ref for session-clear divider
  const copilotTurnRef = useRef(0);
  const latestCopilotMsgRef = useRef(null);
  const [shouldScrollToDivider, setShouldScrollToDivider] = useState(false);
  const [leftPct, setLeftPct] = useState(32);
  
  // Image modal state
  const [zoomImageUrl, setZoomImageUrl] = useState(null);
  const handleImageClick = (url) => setZoomImageUrl(url);
  const closeZoom = () => setZoomImageUrl(null);
  
  const handleResizeMouseDown = (e) => {
    e.preventDefault();
    isResizingRef.current = true;
    document.body.style.cursor = 'col-resize';
    document.body.style.userSelect = 'none';
    const onMouseMove = (e) => {
      if (!isResizingRef.current || !splitContainerRef.current) return;
      const rect = splitContainerRef.current.getBoundingClientRect();
      const pct = ((e.clientX - rect.left) / rect.width) * 100;
      setLeftPct(Math.min(Math.max(pct, 20), 80));
    };
    const onMouseUp = () => {
      isResizingRef.current = false;
      document.body.style.cursor = '';
      document.body.style.userSelect = '';
      document.removeEventListener('mousemove', onMouseMove);
      document.removeEventListener('mouseup', onMouseUp);
    };
    document.addEventListener('mousemove', onMouseMove);
    document.addEventListener('mouseup', onMouseUp);
  };
  
  // Force light theme for TSE Dashboard
  const [isDarkMode] = useState(false);
  
  // Light theme configuration only - matching ChatWindow colors
  const theme = {
    light: {
      bg: 'bg-white',
      sidebarBg: 'bg-gray-50',
      cardBg: 'bg-white',
      headerBg: 'bg-gray-50',
      contentBg: 'bg-white',
      inputBg: 'bg-white',
      text: 'text-[#253439]',
      textSecondary: 'text-gray-600',
      textMuted: 'text-[#A2A9AF]',
      timestampColor: 'text-gray-400',
      border: 'border-[#06b6d4]',
      borderLight: 'border-gray-200',
      hover: 'hover:bg-gray-50',
      buttonBg: 'bg-white',
      buttonHover: 'hover:bg-gray-50',
      accent: 'bg-[#06b6d4]',
      accentHover: 'hover:bg-cyan-700',
      success: 'bg-green-600',
      warning: 'bg-yellow-600',
      danger: 'bg-red-600',
    }
  };
  
  const currentTheme = theme.light;
  
  // No theme toggle for TSE Dashboard
  const toggleTheme = () => {
    // Disabled - TSE Dashboard is light theme only
  };

  const recordDiagMapNode = useCallback((node) => {
    if (!node) return;
    const turn = ++diagMapTurnRef.current;
    setDiagMapTrail((prev) => {
      const id = node.nid || node.id || `${node.type || "node"}-${Date.now()}`;
      const last = prev[prev.length - 1];
      if (last && (last.nid || last.id) === id) return prev;
      return [...prev, { ...node, id, nid: node.nid || id, turn }].slice(-40);
    });
    return turn;
  }, []);

  const openDiagMapFullWindow = useCallback(() => {
    setDiagMapFullFocusNodeId(null);
    setDiagMapFullFocusKey(0);
    setDiagMapFullOpen(true);
    setDiagMapFullMinimized(false);
  }, []);

  const openInlineDiagMapForNode = useCallback((nodeId, maxTurn = null) => {
    setDiagMapFocusNodeId(nodeId || null);
    setDiagMapFocusKey(k => k + 1);
    setDiagMapMaxTurn(maxTurn);
    setDiagMapFullOpen(false);
    setDiagMapFullMinimized(false);
    setDiagMapInlineOpen(true);
    setDiagMapInlineMinimized(false);
  }, []);

  const advanceDiagMapSnapshot = useCallback((newSnap) => {
    if (!newSnap) return;
    // Full view: accumulate traversal path across cache/LLM switches.
    // Use new snapshot's subgraph only when it actually has nodes (prevents
    // a cache hit with empty subgraph from wiping out the full graph structure).
    setDiagMapFullSnapshot((prev) => {
      const prevPath = prev?.traversal_path || [];
      const newPath = newSnap.traversal_path || [];
      const traversal_path = newPath.length >= prevPath.length ? newPath : prevPath;
      const visited = [...new Set([...(prev?.visited_nodes || []), ...(newSnap.visited_nodes || [])])];
      // Keep whichever subgraph is larger: the backend sends the full graph once (step 0),
      // and subsequent updates carry only the visited slice — never replace a bigger graph with a smaller one.
      const prevNodeCount = (prev?.diagnostic_subgraph?.nodes || []).length;
      const newNodeCount = (newSnap.diagnostic_subgraph?.nodes || []).length;
      const diagnostic_subgraph = newNodeCount > prevNodeCount
        ? newSnap.diagnostic_subgraph
        : (prev?.diagnostic_subgraph || newSnap.diagnostic_subgraph);
      return {
        ...newSnap,
        visited_nodes: visited,
        traversal_path,
        visited_node_details: { ...(prev?.visited_node_details || {}), ...(newSnap.visited_node_details || {}) },
        diagnostic_subgraph,
      };
    });
    // Mini view only tracks the visited path. A full-graph-only snapshot (no
    // visited markers — e.g. served from semantic cache) must not pollute it.
    if (!(newSnap.visited_nodes || []).length && !newSnap.current_node_nid) return;
    setDiagMapSnapshot((prev) => {
      // Only include nodes that were actually visited/traversed — not the full
      // possible graph. Applies to the FIRST snapshot too: the raw snapshot
      // carries the whole runbook subgraph, which belongs in the full view only.
      const newVisited = new Set([
        ...(newSnap.visited_nodes || []),
        ...(newSnap.traversal_path || []),
        ...(newSnap.current_node_nid ? [newSnap.current_node_nid] : []),
      ]);

      // Merge nodes — prev keeps all its nodes; from new snap only add visited ones
      const nodeMap = new Map();
      (prev?.diagnostic_subgraph?.nodes || []).forEach(n => { if (n.nid) nodeMap.set(n.nid, n); });
      (newSnap.diagnostic_subgraph?.nodes || [])
        .filter(n => n.nid && (newVisited.size === 0 || newVisited.has(n.nid)))
        .forEach(n => nodeMap.set(n.nid, n));

      // Merge edges — only edges between nodes we actually have
      const edgeMap = new Map();
      const eKey = (e) => `${e.from_nid ?? e.from}|${e.to_nid ?? e.to}`;
      (prev?.diagnostic_subgraph?.edges || []).forEach(e => edgeMap.set(eKey(e), e));
      (newSnap.diagnostic_subgraph?.edges || [])
        .filter(e => {
          const f = e.from_nid ?? e.from;
          const t = e.to_nid ?? e.to;
          return nodeMap.has(f) && nodeMap.has(t);
        })
        .forEach(e => edgeMap.set(eKey(e), e));

      const visited = [...new Set([...(prev?.visited_nodes || []), ...(newSnap.visited_nodes || [])])];
      const prevPath = prev?.traversal_path || [];
      const newPath = newSnap.traversal_path || [];
      const traversal_path = newPath.length >= prevPath.length ? newPath : prevPath;

      return {
        ...newSnap,
        visited_nodes: visited,
        traversal_path,
        visited_node_details: { ...(prev?.visited_node_details || {}), ...(newSnap.visited_node_details || {}) },
        diagnostic_subgraph: {
          nodes: [...nodeMap.values()],
          edges: [...edgeMap.values()],
        },
      };
    });
  }, []);

  const clearDiagMap = useCallback(() => {
    setDiagMapTrail([]);
    setDiagMapSnapshot(null);
    setDiagMapFullSnapshot(null);
    diagMapTurnRef.current = 0;
    setDiagMapMaxTurn(null);
    setDiagMapInlineOpen(false);
    setDiagMapInlineMinimized(false);
    setDiagMapFullOpen(false);
    setDiagMapFullMinimized(false);
    setDiagMapFocusNodeId(null);
    setDiagMapFocusKey(0);
    setDiagMapFullFocusNodeId(null);
    setDiagMapFullFocusKey(0);
  }, []);

  diagMapOpenRef.current = diagMapInlineOpen || diagMapFullOpen;

  // ── Background fetch: load the full knowledge graph for a product ───────
  // Called with { product, graphVersion } from each chat response.
  // Cache key = graphVersion (hash of mermaid files) when available, else product.
  // Fetches once per session; refetches only if the mermaid version changes.
  const fetchKnowledgeGraph = useCallback(({ product, graphVersion } = {}) => {
    if (!product) return;
    const cacheKey = graphVersion || product;
    if (graphFetchedForProductRef.current === cacheKey && fullGraphRef.current) return;
    console.log("[KG] Fetching knowledge graph for product:", product, "version:", graphVersion);
    graphFetchedForProductRef.current = cacheKey;
    setGraphLoading(true);
    authFetch(KNOWLEDGE_GRAPH_ENDPOINT(product))
      .then((res) => {
        if (!res.ok) throw new Error(`KG fetch failed: ${res.status}`);
        return res.json();
      })
      .then((data) => {
        const subgraph = data.diagnostic_subgraph || data;
        if ((subgraph.nodes || []).length) {
          fullGraphRef.current = { nodes: subgraph.nodes, edges: subgraph.edges || [] };
          console.log(`[KG] Cached graph for ${product}: ${subgraph.nodes.length} nodes, ${(subgraph.edges || []).length} edges`);
          if (lastChatDataRef.current) {
            const snap = {
              diagnostic_subgraph: { nodes: subgraph.nodes, edges: subgraph.edges || [] },
              current_node_nid: lastChatDataRef.current.current_node_nid || null,
              traversal_path: lastChatDataRef.current.traversal_path || [],
              visited_nodes: lastChatDataRef.current.traversal_path || [],
              visited_node_details: lastChatDataRef.current.visited_node_details || {},
              eliminated_causes: lastChatDataRef.current.eliminated_causes || [],
            };
            advanceDiagMapSnapshot(snap);
          }
        } else {
          console.warn("[KG] Empty graph returned for product:", product);
          graphFetchedForProductRef.current = null;
        }
      })
      .catch((err) => {
        console.warn("[KG] Failed to fetch knowledge graph:", err.message);
        graphFetchedForProductRef.current = null;
      })
      .finally(() => setGraphLoading(false));
  }, [advanceDiagMapSnapshot]);

  // ── Build a synthetic snapshot from cached graph + chat response fields ──
  // The new chat API returns only: current_node_nid, traversal_path,
  // visited_node_details (no diagnostic_subgraph / session_snapshot).
  // We reconstitute the shape DiagnosticMapWindow expects by combining the
  // locally-cached full graph with those lightweight fields.
  const buildSyntheticSnapshot = useCallback((chatData) => {
    const graph = fullGraphRef.current;
    if (!graph && !chatData.current_node_nid && !chatData.traversal_path) return null;
    return {
      // Graph structure — from cache (may be null on first message before fetch resolves)
      diagnostic_subgraph: graph
        ? { nodes: graph.nodes, edges: graph.edges }
        : undefined,
      // Traversal fields — from chat response
      current_node_nid: chatData.current_node_nid || null,
      traversal_path: chatData.traversal_path || [],
      visited_nodes: chatData.traversal_path || [],
      visited_node_details: chatData.visited_node_details || {},
      eliminated_causes: chatData.eliminated_causes || [],
    };
  }, []);

  // Navigation handler
  const handleNavigation = (path) => {
    navigate(path);
  };

  // Helper to capitalize names
  const capitalize = (str) => {
    if (!str) return '';
    return str.charAt(0).toUpperCase() + str.slice(1).toLowerCase();
  };

  //Since we are not passing TSECopilot Live Agent name in messages to socket.py and then ChatWindow, we need to send the Agentname in the first join message.
  const agentName = localStorage.getItem("username") || "TSE Agent";

  // Handle TSE agent joining session
  const handleJoinSession = (sessionData) => {
    console.log("[TSE] Joining session:", sessionData);
    localStorage.setItem('CustomerName', sessionData.username);
    const s = socketRef.current;
    if (s) {
      s.emit("tseAgentJoinSession", {
        user_sid: sessionData.user_sid,
        session_room: sessionData.session_room,
        //  agent_name: "TSE Agent"
        agent_name: agentName,
      });
    
      // Store current session information for message routing
      setCurrentSession(sessionData);
      
      // Add confirmation message to dashboard
      setDashboardMessages(prev => [...prev, {
        text: `Joined Session: Successfully connected to ${capitalize(sessionData.username)}'s session. You can now assist them directly.`,
        sender: "system",
        timestamp: Date.now()
      }]);
    }
  };

  // Socket connection
  useEffect(() => {
    const newSocket = connectSocket();
    socketRef.current = newSocket;

    const emitCopilotJoin = () => {
      console.log("[SOCKET] TSE Agent joining copilot room only...");
      newSocket.emit("joinCopilotRoom", {
        room_id: "copilot_room",
        role: "agent",
        name: agentName,
        mode: mode || "testpilot",
      });
    };

    newSocket.on("connect", () => {
      console.log("[SOCKET] Connected to server, id=", newSocket.id);
      emitCopilotJoin();
      console.log("[SOCKET] TSE Agent staying out of diagnostic rooms completely");
    });

    newSocket.on("connect_error", (err) => {
      console.error("[SOCKET] connect_error — cannot reach backend:", err?.message || err);
    });

    newSocket.on("reconnect", () => {
      console.log("[SOCKET] Reconnected after backend came online");
      emitCopilotJoin();
    });

    newSocket.on("disconnect", (reason) => {
      console.log("[SOCKET] Disconnected from server:", reason);
      setCurrentSession(null);
    });

    // Listen for messages - these go to dashboard
    // ===== COPILOT (OLD) =====
    // newSocket.on("copilotMessage", (data) => {
    //   console.log("[COPILOT] Received copilot message:", data);
    //   ... (same code below)
    // });
    
    // ===== TESTPILOT (NEW) =====
    newSocket.on("testpilotMessage", (data) => {
      console.log("[TESTPILOT] Received testpilot message:", data);
      
      // Don't stop thinking here - let each message type handle it appropriately
      
      // Check if this is a /clear command response - keep chat visible, only reset backend-facing state
      if (data.session_cleared || data.clear_all_state) {
        console.log("[TESTPILOT] Backend Conversation cleared! — keeping chat history visible");
        
        // Stop all animations on clear
        setIsThinking(false);
        setIsProcessing(false);
        
        // Clear input fields only (conversation stays on screen)
        setDashboardMessage("");
        setCustomerMessage("");
        setWaitingForAnswer(false);
        setCurrentQuestion(null); // Clear question state
        setIsQuestionCollapsed(false); // Reset collapsed state
        clearDiagMap();
        
        // Add divider only if not already added by sendDashboardMessage
        setDashboardMessages((prev) => {
          const last = prev[prev.length - 1];
          if (last && last.type === "session_clear_divider") return prev;
          return [...prev, { type: "session_clear_divider", sender: "system", timestamp: Date.now() }];
        });
        setShouldScrollToDivider(true);
        return; // Don't add the text message — divider handles display
      }

      // ── TSE Interactive: Question widget ──────────────────────────────
      if (data.type === "question") {
        console.log("[TSE] Question received:", data);
        if (data.traversal_path?.length) lastChatDataRef.current = data;
        // Trigger background graph fetch when product is known
        if (data.selected_product) fetchKnowledgeGraph({ product: data.selected_product, graphVersion: data.graph_version });
        // Build synthetic snapshot from lightweight fields + cached graph
        const qSnapshot = buildSyntheticSnapshot(data);
        if (qSnapshot) advanceDiagMapSnapshot(qSnapshot);
        const questionNodeId = `question-${data.timestamp || Date.now()}`;
        recordDiagMapNode({
          id: questionNodeId,
          type: "Decision",
          name: data.question,
          question: data.question,
        });
        if (diagMapOpenRef.current) {
          setDiagMapFocusNodeId(questionNodeId);
          setDiagMapFocusKey(k => k + 1);
        }
        setWaitingForAnswer(true);
        // Store current question state for persistent display above input
        const questionId = data.timestamp || Date.now();
        // Replace old question with new one (intelligent replacement)
        setCurrentQuestion({
          question: data.question,
          options: data.options || [],
          input_type: data.input_type || "single",
          messageId: questionId,
          cache_hit: data.cache_hit || false,
        });
        // Reset collapsed state for new question (always show expanded)
        setIsQuestionCollapsed(false);
        // Stop all animations when question arrives
        setIsThinking(false);
        setIsProcessing(false);
        // Remove any lingering searching animations (but don't add question to chat history)
        setDashboardMessages(prev => 
          prev.filter(m => m.type !== "rag_searching" && m.type !== "rag_results")
        );
        return;
      }

      // ── TSE: Info messages — display in chat ──────────
      if (data.type === "info") {
        console.log("[TSE-info]", data.text);
        if (data.current_node) {
          recordDiagMapNode(getDiagNodeFromCurrentNode(data.current_node, { type: "Info" }));
        } else {
          recordDiagMapNode({
            id: `info-${data.timestamp || Date.now()}`,
            type: "Info",
            name: data.text,
            text: data.text,
          });
        }
        // Check if searches are active - if not, switch to processing mode
        setDashboardMessages(prev => {
          const hasActiveSearches = prev.some(m => m.type === "rag_searching");
          if (!hasActiveSearches) {
            setIsProcessing(true);
            setIsThinking(false);
          }
          return prev;
        });
        setDashboardMessages(prev => [...prev, {
          type: "info",
          text: data.text,
          sender: "testpilot",
          timestamp: data.timestamp || Date.now(),
        }]);
        return;
      }

      // ── TSE: Feedback reminder — separate blob shown after session close ──
      if (data.type === "feedback_prompt") {
        console.log("[TSE-feedback]", data.text);
        setDashboardMessages(prev => [...prev, {
          type: "feedback_prompt",
          text: data.text,
          sender: "testpilot",
          timestamp: data.timestamp || Date.now(),
        }]);
        return;
      }

      // ── TSE: RAG search — show searching animation ────────────────────
      if (data.type === "rag_search") {
        console.log("[TSE-RAG]", data.tool, data.query);
        recordDiagMapNode({
          id: `${data.tool || "rag"}-${data.node_type || "search"}-${data.query || Date.now()}`,
          type: data.node_type || "DiagnosticSteps",
          name: data.query || data.tool || "Knowledge search",
          description: data.tool || "",
        });
        // Stop thinking animation when search starts - only show search-specific animation
        setIsThinking(false);
        setIsProcessing(false);
        // Create unique search ID based on tool and query to prevent duplicates
        const searchId = `${data.tool}_${data.query}_${data.node_type || ''}`;
        setDashboardMessages(prev => {
          // Check if a search with the same tool and query already exists
          const existingSearch = prev.find(
            m => m.type === "rag_searching" && 
            m.tool === data.tool
          );
          
          // Only add if this exact search doesn't already exist
          if (!existingSearch) {
            return [...prev, {
              type: "rag_searching",
              tool: data.tool,
              query: data.query,
              node_type: data.node_type,
              sender: "testpilot",
              timestamp: data.timestamp || Date.now(),
              searchId: searchId, // Add unique identifier
            }];
          }
          return prev; // Don't add duplicate animation for same tool type
        });
        return;
      }

      // ── TSE: RAG chunks arrived — remove searching message, show processing ─────
      if (data.type === "rag_chunks") {
        console.log("[TSE-RAG-chunks]", data.chunks?.length, "chunks");
        // Remove only the specific searching message that matches this tool and node_type
        setDashboardMessages(prev => {
          const filtered = prev.filter(m => {
            // Remove only the search message that matches this tool and node_type (if provided)
            if (m.type === "rag_searching" && m.tool === data.tool) {
              return false; // Remove animation
            }
            return true; // Keep all other messages
          });
          const hasActiveSearches = filtered.some(m => m.type === "rag_searching");
          // If no more searches, switch to processing mode
          if (!hasActiveSearches) {
            setIsProcessing(true);
            setIsThinking(false);
          }
          return filtered;
        });
        return;
      }

      // ── TSE Interactive: Assistant final response ─────────────────────
      if (data.type === "assistant") {
        if (!data.text?.trim()) {
          setIsThinking(false);
          setIsProcessing(false);
          if (data.usage) {
            setUsageStats(prev => ({
              ...prev,
              total_input: prev.total_input + (data.usage.input_tokens || 0),
              total_output: prev.total_output + (data.usage.output_tokens || 0),
              total_cache_write: prev.total_cache_write + (data.usage.cache_write_tokens || 0),
              total_cache_read: prev.total_cache_read + (data.usage.cache_read_tokens || 0),
              total_cost_usd: prev.total_cost_usd + (data.usage.cost_usd || 0),
              total_savings_usd: prev.total_savings_usd + (data.usage.savings_usd || 0),
              requests: prev.requests + 1,
            }));
          }
          if (data.session_totals) setUsageStats(data.session_totals);
          return;
        }
        if (data.traversal_path?.length) lastChatDataRef.current = data;
        // Trigger background graph fetch when product is known
        if (data.selected_product) fetchKnowledgeGraph({ product: data.selected_product, graphVersion: data.graph_version });
        // Build synthetic snapshot from lightweight fields + cached graph
        const snapshot = buildSyntheticSnapshot(data);
        if (snapshot) advanceDiagMapSnapshot(snapshot);
        let diagMapNodeId = null;
        let diagMapTurn = null;
        if (data.current_node) {
          const diagNode = getDiagNodeFromCurrentNode(data.current_node, { type: "Resolution" });
          diagMapNodeId = diagNode?.nid || diagNode?.id || null;
          diagMapTurn = recordDiagMapNode(diagNode);
        } else if (snapshot?.current_node_nid) {
          diagMapNodeId = snapshot.current_node_nid;
          diagMapTurn = diagMapTurnRef.current;
        } else {
          const fallbackNode = {
            id: `assistant-${data.timestamp || Date.now()}`,
            type: "Resolution",
            name: data.text,
            text: data.text,
          };
          diagMapNodeId = fallbackNode.id;
          diagMapTurn = recordDiagMapNode(fallbackNode);
        }
        if (diagMapNodeId && diagMapOpenRef.current) {
          setDiagMapFocusNodeId(diagMapNodeId);
          setDiagMapFocusKey(k => k + 1);
        }
        // Update usage stats if provided
        if (data.usage) {
          setUsageStats(prev => ({
            ...prev,
            total_input: prev.total_input + (data.usage.input_tokens || 0),
            total_output: prev.total_output + (data.usage.output_tokens || 0),
            total_cache_write: prev.total_cache_write + (data.usage.cache_write_tokens || 0),
            total_cache_read: prev.total_cache_read + (data.usage.cache_read_tokens || 0),
            total_cost_usd: prev.total_cost_usd + (data.usage.cost_usd || 0),
            total_savings_usd: prev.total_savings_usd + (data.usage.savings_usd || 0),
            requests: prev.requests + 1,
          }));
        }
        if (data.session_totals) {
          setUsageStats(data.session_totals);
        }
        // Remove any lingering searching animations before adding the reply
        setDashboardMessages(prev => [
          ...prev.filter(m => m.type !== "rag_searching" && m.type !== "rag_results"),
          {
            text: data.text,
            sender: data.sender || "copilot",
            timestamp: data.timestamp || Date.now(),
            usage: data.usage,
            diagMapNodeId,
            diagMapTurn,
            cache_hit: data.cache_hit || false,
          },
        ]);
        // Stop all animations when assistant response arrives
        setIsThinking(false);
        setIsProcessing(false);
        // Clear question state if this is a regular response (not a question)
        // This indicates conversation moved on to a new topic
        if (currentQuestion) {
          setCurrentQuestion(null);
          setWaitingForAnswer(false);
          setIsQuestionCollapsed(false); // Reset collapsed state
        }
        // Auto-trigger implicit search: cases on turns 3,6,9; bugs on turns 4,7,10
        copilotTurnRef.current += 1;
        const turn = copilotTurnRef.current;
        if (turn >= 3 && turn % 3 === 0 && socketRef.current) {
          socketRef.current.emit("implicitSearch", { type: "cases", top_k: 5 });
        }
        if (turn >= 4 && (turn - 1) % 3 === 0 && socketRef.current) {
          socketRef.current.emit("implicitSearch", { type: "bugs", top_k: 5 });
        }
        return;
      }

      // Check if this is a search_rag action that needs to open TSEAgentRag
      if (data.action === 'search_rag' && data.text && data.text.includes("Opening TSEAgentRag")) {
        console.log("[ACTION] Auto-opening TSEAgentRag in new tab for search functionality");
        const tseAgentRagUrl = `${window.location.origin}/TseAgentRag`;
        window.open(tseAgentRagUrl, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
      }
      
      // Add auto-trigger notification for detected actions
      if (data.action && data.confidence) {
        const actionName = data.action.replace('_', ' ').replace(/\b\w/g, l => l.toUpperCase());
        setDashboardMessages(prev => [...prev, {
          text: `Auto-triggered: ${actionName} action (confidence: ${data.confidence})`,
          sender: "system",
          timestamp: Date.now()
        }]);
      }
      
      // Only add message if it has text and is not a silent clear
      if (data.text && !data.silent) {
        if (data.traversal_path?.length) lastChatDataRef.current = data;
        // Trigger background graph fetch when product is known
        if (data.selected_product) fetchKnowledgeGraph({ product: data.selected_product, graphVersion: data.graph_version });
        // Build synthetic snapshot from lightweight fields + cached graph
        const snapshot = buildSyntheticSnapshot(data);
        if (snapshot) advanceDiagMapSnapshot(snapshot);
        let diagMapNodeId = null;
        let diagMapTurn = null;
        if (data.current_node) {
          const diagNode = getDiagNodeFromCurrentNode(data.current_node, { type: "Workflow" });
          diagMapNodeId = diagNode?.nid || diagNode?.id || null;
          diagMapTurn = recordDiagMapNode(diagNode);
        } else if (snapshot?.current_node_nid) {
          diagMapNodeId = snapshot.current_node_nid;
          diagMapTurn = diagMapTurnRef.current;
        }
        if (diagMapNodeId && diagMapOpenRef.current) {
          setDiagMapFocusNodeId(diagMapNodeId);
          setDiagMapFocusKey(k => k + 1);
        }
        setDashboardMessages(prev => [...prev, {
          text: data.text,
          sender: data.sender || "copilot",
          timestamp: data.timestamp || Date.now(),
          diagMapNodeId,
          diagMapTurn,
          cache_hit: data.cache_hit || false,
        }]);
      }
    });

    // Listen for new copilot session notifications
    newSocket.on("newCopilotSession", (data) => {
      console.log("[COPILOT] New copilot session available:", data);
      setDashboardMessages(prev => [...prev, {
        text: `New Session Available: ${data.username} has entered copilot mode and is waiting for assistance. Click "Join Session" to connect.`,
        sender: "system",
        timestamp: Date.now(),
        sessionData: data
      }]);
    });

    // Listen for live agent messages (customer messages only)
    newSocket.on("liveAgentMessage", (data) => {
      console.log("[LIVE-AGENT] Received liveAgentMessage:", data);
      console.log("[LIVE-AGENT] Sender:", data.sender);
      
      // Only add customer messages to customer chat
      if (data.sender === "user" || data.sender === "customer") {
        setCustomerMessages(prev => [...prev, {
          text: data.text,
          sender: data.sender || "user",
          agentName: data.agentName,
          userName: data.userName,
          timestamp: data.timestamp || Date.now()
        }]);
      }
    });

    newSocket.on("implicitSearchResults", (data) => {
      console.log("[IMPLICIT-SEARCH] Results:", data);
      if (data.cases?.length) {
        setImplicitCases(data.cases);
        setHasNewCases(true);
      }
      if (data.bugs?.length) {
        setImplicitBugs(data.bugs);
        setHasNewBugs(true);
      }
      setLoadingMoreCases(false);
      setLoadingMoreBugs(false);
    });

    // Debug: Log all socket events for troubleshooting
    newSocket.onAny((eventName, ...args) => {
      console.log(`[SOCKET] Event: ${eventName}`, args);
    });

    // Connect may have fired before listeners were attached
    if (newSocket.connected) {
      emitCopilotJoin();
    }

    return () => {
      socketRef.current = null;
      if (dashboardSendErrorTimerRef.current) {
        window.clearTimeout(dashboardSendErrorTimerRef.current);
        dashboardSendErrorTimerRef.current = null;
      }
      newSocket.disconnect();
    };
  }, [agentName, mode, clearDiagMap, recordDiagMapNode, fetchKnowledgeGraph, buildSyntheticSnapshot]); // eslint-disable-line react-hooks/exhaustive-deps -- avoid duplicate socket listeners; handlers read currentQuestion from render closure

  // Format timestamp
  const formatTimestamp = (timestamp) => {
    const date = new Date(timestamp);
    return date.toLocaleTimeString([], { hour: '2-digit', minute: '2-digit' });
  };

  // Format bot message
  const formatBotMessage = (text) => {
    // Enhanced formatting for clean pipeline output
    // Remove reference node notes (lines starting with '**Note -')
    let filteredText = text
      .split('\n')
      .filter(line => !/^\*\*Note\s*[-:]/.test(line.trim()) && !/^Note\s*:/i.test(line.trim()) && !/^\*\*TL;?DR\*?\*?/i.test(line.trim()))
      .join('\n');

    let formattedText = filteredText;
    // 1. Convert markdown image syntax ![alt text](url) to modal-friendly links
    formattedText = formattedText.replace(
      /!\[([^\]]*)\]\((https?:\/\/[^)]+)\)/g,
      '<a tabindex="0" class="image-link text-blue-600 hover:text-blue-800 underline" data-image-url="$2" style="cursor:pointer;">$1</a>'
    );
    // 2. Convert Google Drive image references to clickable "image" links with data attribute
    formattedText = formattedText.replace(
      /!\[\]\[?(https?:\/\/drive\.google\.com\/file\/d\/([a-zA-Z0-9_-]+)\/view[^\]]*)\]?/g,
      '<a tabindex="0" class="image-link text-blue-600 hover:text-blue-800 underline" data-image-url="https://drive.google.com/file/d/$2/view?usp=sharing" style="cursor:pointer;">image</a>'
    );
    // 3. Also handle already-formatted <a> tags with "image" text (from pipeline)
    // and markdown-style [image](url) links
    formattedText = formattedText.replace(
      /\[image\]\((https?:\/\/[^)]+)\)/g,
      '<a class="image-link text-blue-600 hover:text-blue-800 underline" data-image-url="$1" style="cursor:pointer;">image</a>'
    );

    // 2. Strip any remaining markdown heading markers (###, ##, #)
    formattedText = formattedText.replace(/^#{1,3}\s+/gm, '');
    
    // 3. Convert bold markers **text** → <strong>text</strong> (safety net if LLM still uses markdown)
    formattedText = formattedText.replace(/\*\*(.*?)\*\*/g, '<strong>$1</strong>');
    // 4. Strip italic markers *text* → text
    formattedText = formattedText.replace(/\*(.*?)\*/g, '$1');
    // 5. Strip bullet dashes at start of line (- item → item)
    formattedText = formattedText.replace(/^\s*[-•]\s+/gm, '  ');
    // 6. Convert markdown links [text](url) to clickable HTML
    formattedText = formattedText.replace(/\[([^\]]+)\]\((https?:\/\/[^)]+)\)/g, (match, label, url) => {
      if (isImagePreviewLink(label, url)) {
        return `<a tabindex="0" class="image-link text-blue-600 hover:text-blue-800 underline" data-image-url="${url}" style="cursor:pointer;">${label}</a>`;
      }

      return `<a href="${url}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">${label}</a>`;
    });
    // 6b. Fallback: convert bare Source: URL lines to a labeled link
    formattedText = formattedText.replace(
      /(<strong>Source:<\/strong>\s*)(https?:\/\/[^\s<]+)/g,
      '$1<a href="$2" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">Source Document</a>'
    );
    // 7. URL detection — make bare URLs clickable
    // Stop at &lt; / &gt; HTML entities so escaped angle brackets aren't swallowed into the URL
    const urlRegex = /(?<!")\b(https?:\/\/(?:[^\s<>)"&]|&(?!lt;|gt;))+)(?!")/gi;
    formattedText = formattedText.replace(urlRegex, (match) => {
      // Skip if already inside an anchor tag
      return `<a href="${match}" target="_blank" rel="noopener noreferrer" class="text-blue-600 hover:text-blue-800 underline">${match}</a>`;
    });
    // 8. Convert markdown tables to HTML tables
    // Match markdown table format: | col1 | col2 | ... | followed by |---|---| and data rows
    const tableRegex = /(\|.+\|(?:\n\|[:\s\-|]+\|)?(?:\n\|.+\|)+)/g;
    formattedText = formattedText.replace(tableRegex, (match) => {
      const lines = match.trim().split('\n').filter(l => l.trim());
      if (lines.length < 2) return match; // Need at least header + separator
      
      let html = '<table class="border-collapse border border-gray-300 my-3" style="width: 100%; font-size: 13px;"><thead><tr>';
      const headerRow = lines[0].split('|').filter(c => c.trim());
      headerRow.forEach(cell => {
        html += `<th class="border border-gray-300 px-3 py-2 bg-gray-100 font-semibold text-left">${cell.trim()}</th>`;
      });
      html += '</tr></thead><tbody>';
      
      // Skip separator row (index 1) and process data rows
      for (let i = 2; i < lines.length; i++) {
        const cells = lines[i].split('|').filter(c => c.trim());
        html += '<tr>';
        for (const cell of cells) {
          html += `<td class="border border-gray-300 px-3 py-2">${cell.trim()}</td>`;
        }
        html += '</tr>';
      }
      html += '</tbody></table>';
      return html;
    });
    
    // 8b. Convert diagnosis section headers to styled section labels
    formattedText = formattedText.replace(
      /<strong>(What's Happening|Root Cause|Next Steps|Escalation Reality|Sources Selected|Recommended Path|Alternate Routes|Answer|Diagnosis Plan|My Pick:[^<]*|Recommended:[^<]*)<\/strong>/g,
      '<div style="margin-top:12px;margin-bottom:4px;padding:3px 10px;background:#f0f9ff;border-left:3px solid #0891b2;font-weight:600;font-size:13px;color:#155e75;border-radius:0 4px 4px 0;">$1</div>'
    );
    // 8c. Legacy section headers
    formattedText = formattedText.replace(/^(Diagnostic Procedure|Current State|Current Position|Interpretation|Next Step|After This|Root Cause|Available Fixes|Workaround Options|Permanent Fix|Important|What we are checking|Steps|What to Observe|Next|ROOT CAUSE|Resolution Options|Recommended immediate action):/gm, '<strong>$1:</strong>');
    // Remove any stray "Response Type: ..." lines the LLM might still emit
    formattedText = formattedText.replace(/^Response Type:.*$/gm, '');
    // 9. Preserve horizontal rules (emojis are removed by backend sanitization)
    formattedText = formattedText.replace(/^---$/gm, '<hr class="my-3 border-gray-300">');
    // 10. Add line breaks for readability (but preserve table HTML)
    formattedText = formattedText.replace(/\n/g, '<br>');
    return formattedText;
  };

  // Send message function for customer chat
  const sendCustomerMessage = () => {
    const sock = socketRef.current;
    if (!customerMessage.trim() || !sock?.connected) return;

    const newMessage = {
      text: customerMessage,
      sender: "agent",
      timestamp: Date.now()
    };

    // Add message to customer chat state
    setCustomerMessages(prev => [...prev, newMessage]);
    
    // Emit to customer chat system (not copilot)
    console.log("[TSE] Sending customer message:", customerMessage);
    
    // Use current session info if available, otherwise fallback to "all"
    const targetUserSid = currentSession?.user_sid || "all";
    console.log("[TSE] Sending to user_sid:", targetUserSid);
    
    sock.emit("agentMessage", { 
      text: customerMessage, 
      user_sid: targetUserSid,  // Send to specific user if session is active
      target: "customer"
    });
    setCustomerMessage("");
    
    // Reset textarea height to normal size after sending
    setTimeout(() => {
      const customerInput = document.querySelector('textarea[placeholder*="Ask follow-up"]');
      if (customerInput) {
        customerInput.style.height = '28px';
      }
    }, 0);
  };

  // Send message function for dashboard
  const sendDashboardMessage = () => {
    if (!dashboardMessage.trim()) return;
    const sock = socketRef.current;
    if (!sock?.connected) {
      setDashboardSendError(
        "Not connected to the server. Wait until the API on port 8000 shows “Application startup complete”, then refresh this page."
      );
      if (dashboardSendErrorTimerRef.current) {
        window.clearTimeout(dashboardSendErrorTimerRef.current);
      }
      dashboardSendErrorTimerRef.current = window.setTimeout(() => {
        setDashboardSendError("");
        dashboardSendErrorTimerRef.current = null;
      }, 8000);
      return;
    }
    setDashboardSendError("");
    const customerUsername =
      currentSession?.username ||
      (currentSession ? localStorage.getItem("CustomerName") : null) ||
      loggedInUsername ||
      localStorage.getItem("username") ||
      "User";
    const targetUserSid = currentSession?.user_sid || null;

    const trimmedMessage = dashboardMessage.trim().toLowerCase();
    const originalMessage = dashboardMessage.trim();
    
    // Check for /clear command - handle silently before adding to UI
    if (trimmedMessage === "/clear" || trimmedMessage === "clear") {
      console.log("[TSE] /clear command detected - sending to backend silently");
      firstPromptRef.current = null;
      // Add session-clear divider and scroll to it (keep history above)
      setDashboardMessages((prev) => [...prev, {
        type: "session_clear_divider",
        sender: "system",
        timestamp: Date.now()
      }]);
      setShouldScrollToDivider(true);
      // Send to backend to wipe LangGraph memory
      sock.emit("testpilotMessage", { 
        text: dashboardMessage,
        mode: mode || "testpilot",
        username: customerUsername,
        user_sid: targetUserSid,
      });
      // Clear input field and question state
      setDashboardMessage("");
      setCurrentQuestion(null);
      setWaitingForAnswer(false);
      setIsQuestionCollapsed(false); // Reset collapsed state
      clearDiagMap();
      return;
    }

    // If we have a current question with options, check if user is selecting an option
    if (currentQuestion && currentQuestion.options.length > 0) {
      // Check if message matches an option (exact match, case-insensitive, or number)
      const optionIndex = currentQuestion.options.findIndex((opt, idx) => {
        const optLower = opt.toLowerCase();
        return optLower === trimmedMessage || 
               optLower.includes(trimmedMessage) ||
               trimmedMessage === String(idx + 1) ||
               trimmedMessage === `option ${idx + 1}` ||
               trimmedMessage === `option${idx + 1}`;
      });
      
      if (optionIndex !== -1) {
        // User selected an option - submit as answer
        console.log("[TSE] User selected option:", currentQuestion.options[optionIndex]);
        submitAnswer(currentQuestion.options[optionIndex]);
        setDashboardMessage("");
        return;
      }
      // Otherwise, treat as clarifying question or new question - send to backend
      // Backend will determine if it's clarifying (keep options) or new (replace options)
      console.log("[TSE] User typed message while question active - sending to backend for analysis");
    }

    const newMessage = {
      text: originalMessage,
      sender: "agent",
      timestamp: Date.now()
    };

    if (!firstPromptRef.current) firstPromptRef.current = originalMessage;

    // Add message to dashboard chat state
    setDashboardMessages(prev => [...prev, newMessage]);
    recordDiagMapNode({
      id: `agent-${Date.now()}`,
      type: "Symptom",
      name: originalMessage,
      text: originalMessage,
    });

    // Send message to backend
    console.log("[TSE] Sending dashboard message:", originalMessage);
    
    // Initialize session start for TSE mode
    if (mode === "tse" && !sessionStart) {
      setSessionStart(Date.now());
    }
    
    // ===== COPILOT (OLD) =====
    // socket.emit("copilotMessage", { 
    //   text: dashboardMessage,
    //   room_id: "copilot_room",
    //   target: "copilot"
    // });
    
    // ===== TESTPILOT (NEW) =====
    sock.emit("testpilotMessage", { 
      text: originalMessage,
      mode: mode || "testpilot",
      username: customerUsername,
      user_sid: targetUserSid,
    });
    
    // Don't clear waitingForAnswer - let backend decide
    // If backend sends new question, it will update currentQuestion
    // If backend sends info/clarification, options stay visible
    setIsThinking(true); // Start thinking animation
    setIsProcessing(false); // Reset processing state
    setDashboardMessage("");
  };

  // Submit answer to TSE agent question widget
  const submitAnswer = useCallback((answer) => {
    const sock = socketRef.current;
    if (!sock?.connected) return;
    const customerUsername =
      currentSession?.username ||
      (currentSession ? localStorage.getItem("CustomerName") : null) ||
      loggedInUsername ||
      localStorage.getItem("username") ||
      "User";
    const targetUserSid = currentSession?.user_sid || null;
    const questionSnapshot = currentQuestion
      ? {
          question: currentQuestion.question,
          options: currentQuestion.options || [],
          input_type: currentQuestion.input_type || "single",
          cache_hit: currentQuestion.cache_hit || false,
        }
      : null;
    const shouldRenderAnsweredQuestionCard = Boolean(questionSnapshot?.options?.length);

    // Preserve the full question card in chat history when an option is selected.
    setDashboardMessages(prev => [...prev, {
      type: shouldRenderAnsweredQuestionCard ? "answered_question" : undefined,
      text: answer,
      questionSnapshot: shouldRenderAnsweredQuestionCard ? questionSnapshot : null,
      selectedAnswer: answer,
      sender: "agent",
      timestamp: Date.now(),
    }]);
    setWaitingForAnswer(false);
    setCurrentQuestion(null); // Clear question state when answer is submitted
    setIsQuestionCollapsed(false); // Reset collapsed state
    setIsThinking(true);
    setIsProcessing(false);

    // Send answer back to TSE agent via the same testpilotMessage channel
    sock.emit("testpilotMessage", {
      text: answer,
      mode: mode || "testpilot",
      username: customerUsername,
      user_sid: targetUserSid,
    });
  }, [mode, currentQuestion, currentSession, loggedInUsername]);

  // Handle copilot action buttons
  const handleCopilotAction = (action) => {
    console.log(`[COPILOT] Action triggered: ${action}`);
    
    // Special handling for search_rag action - open TSEAgentRag in new tab
    if (action === 'search_rag') {
      console.log("[COPILOT] Opening TSEAgentRag in new tab for search functionality");
      const tseAgentRagUrl = `${window.location.origin}/TseAgentRag`;
      window.open(tseAgentRagUrl, '_blank', 'width=1200,height=800,scrollbars=yes,resizable=yes');
      return;
    }
    
    // Add action message to dashboard chat
    const actionMessage = {
      text: `Action triggered: ${action}`,
      sender: "agent",
      timestamp: Date.now()
    };
    
    setDashboardMessages(prev => [...prev, actionMessage]);
    
    // Emit action to backend via socket
    const sock = socketRef.current;
    if (sock?.connected) {
      sock.emit("copilotAction", {
        action: action,
        room_id: "copilot_room",
        timestamp: Date.now()
      });
    }
  };

  // Handle Enter key for customer chat
  const handleCustomerKeyPress = (e) => {
    if (e.key === "Enter" && !e.shiftKey) {
      e.preventDefault();
      sendCustomerMessage();
    }
  };

  // Handle Enter key for dashboard
  const handleDashboardKeyPress = (e) => {
    if (e.nativeEvent.isComposing || e.key === "Process") {
      return;
    }
    const isEnter =
      e.key === "Enter" || e.code === "Enter" || e.code === "NumpadEnter";
    if (isEnter && !e.shiftKey) {
      e.preventDefault();
      sendDashboardMessage();
    }
  };

  // Auto-resize textarea
  const autoResizeTextarea = (textarea) => {
    textarea.style.height = 'auto';
    textarea.style.height = Math.min(textarea.scrollHeight, 120) + 'px';
  };

  // Auto-scroll to bottom when new messages arrive
  useEffect(() => {
    if (shouldScrollToDivider && clearDividerRef.current) {
      setTimeout(() => {
        clearDividerRef.current?.scrollIntoView({ behavior: 'smooth', block: 'start' });
      }, 50);
      setShouldScrollToDivider(false);
    }
  }, [shouldScrollToDivider, dashboardMessages]);

  useEffect(() => {
    if (customerChatBodyRef.current) {
      customerChatBodyRef.current.scrollTop = customerChatBodyRef.current.scrollHeight;
    }
  }, [customerMessages]);

  useEffect(() => {
    if (latestCopilotMsgRef.current) {
      latestCopilotMsgRef.current.scrollIntoView({ behavior: "smooth", block: "start" });
    } else if (dashboardChatBodyRef.current) {
      dashboardChatBodyRef.current.scrollTop = dashboardChatBodyRef.current.scrollHeight;
    }
  }, [dashboardMessages, isThinking]);

  useEffect(() => {
    const close = (e) => {
      if (!e.target.closest(".relative")) { setShowCasesDropdown(false); setShowBugsDropdown(false); }
    };
    document.addEventListener("mousedown", close);
    return () => document.removeEventListener("mousedown", close);
  }, []);

  return (
    <div className={`flex min-h-screen h-screen w-full ${currentTheme.bg} overflow-hidden transition-colors duration-300`}>
      {/* Sidebar */}
      <SideBar 
        mode="agent" 
        onLogout={onLogout} 
        isDarkMode={isDarkMode} 
        onToggleTheme={toggleTheme}
        onLinkClick={handleNavigation}
        currentPath={location.pathname}
      />
      
      {/* Main Content */}
      <div className="flex-1 flex flex-col h-full relative">
        {/* Profile + Keys */}
        <div className="absolute top-4 right-4 z-50">
          <UserProfileMenu onLogout={onLogout} />
        </div>

        {/* Main Content */}
        <div ref={splitContainerRef} className="flex h-full">
          {/* Chat Pane (Left) - resizable */}
          <div className="flex flex-col" style={{ width: `${leftPct}%`, minWidth: 0 }}>
            <div className={`flex-1 flex flex-col ${currentTheme.bg} h-full`}>
              {/* Chat Header */}
              <div className={`${currentTheme.headerBg} ${currentTheme.border} border-b px-4 py-3 h-[76px] flex-shrink-0 flex flex-col justify-center`}>
                <div className="flex justify-between items-center">
                  <div className="min-w-0 flex-1">
                    <h2 className={`text-lg font-semibold ${currentTheme.text} truncate`}>Customer Chat</h2>
                    <p className={`text-sm ${currentTheme.textSecondary} truncate mt-0.5`} title="Live conversation with customer">Live conversation with customer</p>
                  </div>
                  {currentSession && (
                    <div className="flex items-center gap-2 flex-shrink-0 ml-4">
                      <div className="flex items-center gap-2 px-3 py-1 bg-green-100 text-green-700 rounded-full text-xs font-medium">
                        <div className="w-2 h-2 bg-green-500 rounded-full"></div>
                        Connected to {capitalize(currentSession.username)}
                      </div>
                      <button
                        onClick={() => {
                          setCurrentSession(null);
                          setCustomerMessages([]);
                          setDashboardMessages(prev => [...prev, {
                            text: "Disconnected: Session ended. You can join a new session when available.",
                            sender: "system",
                            timestamp: Date.now()
                          }]);
                          const sock = socketRef.current;
                          if (sock?.connected) {
                            sock.emit("clear_context_memory", { sessionId: currentSession?.id });
                          }
                        }}
                        className="px-2 py-1 text-xs text-red-600 hover:text-red-700 hover:bg-red-50 rounded transition"
                        title="Disconnect from session"
                      >
                        Disconnect
                      </button>
                    </div>
                  )}
                </div>
              </div>

              {/* Customer Chat Messages */}
              <div 
                ref={customerChatBodyRef}
                className="flex-1 overflow-y-auto p-4 space-y-4"
                onClick={(e) => {
                  const imageLink = e.target.closest('a.image-link');
                  if (imageLink) {
                    e.preventDefault();
                    e.stopPropagation();
                    const imageUrl = imageLink.getAttribute('data-image-url');
                    if (imageUrl) {
                      handleImageClick(imageUrl);
                    }
                  }
                }}
              >
                {customerMessages.length === 0 ? (
                  <div className={`text-center ${currentTheme.textSecondary} mt-8`}>
                    <svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="mx-auto mb-4 text-gray-400">
                      <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z"/>
                    </svg>
                    <p>Start the conversation by joining a Copilot chat request!</p>
                  </div>
                ) : (
                  customerMessages.map((msg, index) => (
                    <div
                      key={index}
                      className="w-full flex flex-col mb-4 items-start"
                    >
                      <div
                        className={`px-5 py-4 rounded-2xl max-w-[85%] break-words border shadow-sm ${
                          msg.sender === "agent"
                            ? "bg-purple-50 text-purple-900 border-purple-300"
                            : msg.sender === "user"
                              ? "bg-[#06b6d4] text-white border-[#06b6d4]"
                              : "bg-white text-cyan-900 border-cyan-300"
                        }`}
                        style={{ 
                          fontFamily: "'Space Grotesk', ui-sans-serif, system-ui, -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif",
                          fontSize: "14px"
                        }}
                      >
                        <div dangerouslySetInnerHTML={{ __html: formatBotMessage(msg.displayText || msg.text) }} />
                      </div>
                      <div className="mt-1 text-xs text-gray-400 font-medium" style={{maxWidth: '85%'}}>
                        {msg.sender === "agent" 
                          // ? `${capitalize(localStorage.getItem('username'))} ${formatTimestamp(msg.timestamp)}`
                          ? `You ${formatTimestamp(msg.timestamp)}`

                          : msg.sender === "user" 
                            ? `${capitalize(localStorage.getItem('CustomerName'))} ${formatTimestamp(msg.timestamp)}`
                            : `Copilot ${formatTimestamp(msg.timestamp)}`
                        }
                      </div>
                    </div>
                  ))
                )}
              </div>

              {/* Customer Chat Message Input */}
              <div className={`bg-white rounded-xl border shadow-md flex items-start px-6 py-4 mx-4 mb-6 flex-shrink-0 ${
                currentSession ? 'border-[#06b6d4]' : 'border-gray-300'
              }`}>
                <textarea
                  className={`flex-1 bg-transparent border-none outline-none resize-none overflow-y-auto ${
                    currentSession 
                      ? 'text-[#253439] placeholder-[#A2A9AF] focus:ring-0' 
                      : 'text-gray-400 placeholder-gray-400 cursor-not-allowed'
                  }`}
                  style={{ 
                    minHeight: '28px', 
                    maxHeight: '200px', 
                    height: '28px',
                    fontFamily: "'Space Grotesk', ui-sans-serif, system-ui, -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif",
                    fontSize: "14px"
                  }}
                  placeholder={currentSession ? "Ask follow-up, or type a new question..." : "Connect to a session to start chatting..."}
                  value={customerMessage}
                  onChange={(e) => {
                    if (currentSession) {
                      setCustomerMessage(e.target.value);
                      autoResizeTextarea(e.target);
                    }
                  }}
                  onKeyDown={handleCustomerKeyPress}
                  disabled={!currentSession}
                />
                {/* Send Button */}
                <button
                  className={`ml-2 rounded-lg p-2 transition ${
                    currentSession 
                      ? 'bg-[#06b6d4] hover:bg-cyan-700' 
                      : 'bg-gray-300 cursor-not-allowed'
                  }`}
                  aria-label="Send"
                  onClick={sendCustomerMessage}
                  disabled={!currentSession}
                  type="button"
                >
                  <svg width="20" height="20" fill="none" stroke={currentSession ? "white" : "gray"} strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M22 2 11 13" />
                    <path d="M22 2l-7 20-4-9-9-4z" />
                  </svg>
                </button>
              </div>
            </div>
          </div>

          {/* Drag Handle */}
          <div
            onMouseDown={handleResizeMouseDown}
            className="w-px flex-shrink-0 bg-transparent cursor-col-resize relative group"
            title="Drag to resize"
          >
            <div className="absolute inset-y-0 left-1/2 -translate-x-1/2 w-px bg-gray-300/30 group-hover:bg-cyan-400/60 transition-colors duration-150" />
          </div>

          {/* Copilot Dashboard Pane (Right) - resizable */}
          <div className="relative flex flex-col h-full" style={{ width: `${100 - leftPct}%`, minWidth: 0 }}>
            <div className={`${currentTheme.headerBg} ${currentTheme.border} border-b px-4 py-3 h-[76px] flex-shrink-0 flex flex-col justify-center`}>
              <div>
                <h2 className={`text-lg font-semibold ${currentTheme.text} truncate`}>Copilot Dashboard</h2>
                <div className="flex items-center gap-2 mt-0.5 text-sm min-w-0">
                  <p className={`text-sm ${currentTheme.textSecondary} truncate`} title="AI-powered assistance for TSE">AI-powered assistance for TSE</p>
                  {dashboardMessages.length > 0 && (
                    <>
                      <span className="text-gray-300 text-xs select-none">·</span>
                      <button
                        onClick={() => downloadSessionMarkdown(dashboardMessages)}
                        className="flex items-center gap-1 text-[10px] text-gray-400 hover:text-[#06b6d4] hover:bg-cyan-50 px-1.5 py-0.5 rounded transition whitespace-nowrap"
                        title="Export conversation as Markdown"
                      >
                        <svg width="11" height="11" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                          <path strokeLinecap="round" strokeLinejoin="round" d="M12 10v6m0 0l-3-3m3 3l3-3M6 20h12a2 2 0 002-2V8l-6-6H6a2 2 0 00-2 2v14a2 2 0 002 2z"/>
                        </svg>
                        Export .md
                      </button>
                    </>
                  )}
                </div>
              </div>
            </div>

            {/* Action Buttons */}
            <div className="px-4 py-3 border-b border-gray-200 bg-gray-50">
              <div className="flex flex-wrap gap-2">
                {/* All buttons in one row */}
                <button
                  onClick={() => handleCopilotAction('find_bugs')}
                  className="px-2 py-1.5 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 border border-cyan-200 rounded-md text-xs font-medium transition-colors duration-200 flex items-center gap-1"
                >
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M12 8v4m0 4h.01"/>
                    <circle cx="12" cy="12" r="10"/>
                  </svg>
                  Find Bugs
                </button>

                <button
                  onClick={() => handleCopilotAction('find_cases')}
                  className="px-2 py-1.5 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 border border-cyan-200 rounded-md text-xs font-medium transition-colors duration-200 flex items-center gap-1"
                >
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M9 12l2 2 4-4"/>
                    <rect x="3" y="3" width="18" height="18" rx="2"/>
                  </svg>
                  Find Cases
                </button>

                <button
                  disabled
                  className="px-2 py-1.5 bg-gray-100 text-gray-400 border border-gray-200 rounded-md text-xs font-medium flex items-center gap-1 cursor-not-allowed opacity-60"
                >
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M5 12h14"/>
                    <path d="m12 5 7 7-7 7"/>
                  </svg>
                  Next Step
                </button>

                <button
                  className="px-2 py-1.5 bg-gray-100 text-gray-400 border border-gray-200 rounded-md text-xs font-medium flex items-center gap-1 cursor-not-allowed opacity-60"
                >
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M9.59 4.59A2 2 0 1 1 11 8H2m10.59 11.41A2 2 0 1 0 14 16H2m15.73-8.27A2 2 0 1 1 18 12H2"/>
                  </svg>
                  Possible Causes
                </button>

                <button
                  disabled
                  className="px-1.5 py-1.5 bg-gray-100 text-gray-400 border border-gray-200 rounded-md text-xs font-medium flex items-center gap-1 cursor-not-allowed opacity-60"
                >
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="m3 9 9-7 9 7v11a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z"/>
                    <polyline points="9,22 9,12 15,12 15,22"/>
                  </svg>
                  Analyze Cmd Output
                </button>

                <button
                  onClick={() => handleCopilotAction('search_rag')}
                  className="px-2 py-1.5 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 border border-cyan-200 rounded-md text-xs font-medium transition-colors duration-200 flex items-center gap-1"
                >
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="11" cy="11" r="8"/>
                    <path d="m21 21-4.35-4.35"/>
                  </svg>
                  Search
                </button>

                <button
                  onClick={() => {
                    const INCIDENT_QUERY = "Are there any active Zscaler service incidents right now?";
                    const sock = socketRef.current;
                    if (!sock?.connected) return;
                    const customerUsername =
                      currentSession?.username ||
                      (currentSession ? localStorage.getItem("CustomerName") : null) ||
                      loggedInUsername ||
                      localStorage.getItem("username") ||
                      "User";
                    const targetUserSid = currentSession?.user_sid || null;
                    if (!firstPromptRef.current) firstPromptRef.current = INCIDENT_QUERY;
                    setDashboardMessages(prev => [...prev, { text: INCIDENT_QUERY, sender: "agent", timestamp: Date.now() }]);
                    setIsThinking(true);
                    setIsProcessing(false);
                    sock.emit("testpilotMessage", {
                      text: INCIDENT_QUERY,
                      mode: mode || "testpilot",
                      username: customerUsername,
                      user_sid: targetUserSid,
                    });
                  }}
                  className="px-2 py-1.5 bg-cyan-50 hover:bg-cyan-100 text-cyan-700 border border-cyan-200 rounded-md text-xs font-medium transition-colors duration-200"
                  type="button"
                >
                  Active Incident
                </button>

                <button
                  onClick={openDiagMapFullWindow}
                  className="px-2 py-1.5 bg-violet-50 hover:bg-violet-100 text-violet-700 border border-violet-200 rounded-md text-xs font-medium transition-colors duration-200 flex items-center gap-1"
                  title="Open diagnostic map"
                  type="button"
                >
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <circle cx="12" cy="5" r="3" /><circle cx="5" cy="19" r="3" /><circle cx="19" cy="19" r="3" />
                    <path d="M12 8v3M7.5 17.5L10.5 11M16.5 17.5L13.5 11" />
                  </svg>
                  Diag Map
                </button>
                <button
                  onClick={openFeedbackModal}
                  className="px-2 py-1.5 bg-violet-600 hover:bg-violet-700 text-white rounded-md text-xs font-medium transition-colors duration-200 flex items-center gap-1"
                  title="Give feedback"
                  type="button"
                >
                  <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                    <path d="M21 15a2 2 0 0 1-2 2H7l-4 4V5a2 2 0 0 1 2-2h14a2 2 0 0 1 2 2z" />
                  </svg>
                  Feedback
                </button>

                {implicitBugs.length > 0 && (
                  <div className="relative">
                    <button
                      onClick={() => { setShowBugsDropdown(v => !v); setShowCasesDropdown(false); if (hasNewBugs) setHasNewBugs(false); }}
                      className={`px-2 py-1.5 border rounded-md text-xs font-medium flex items-center gap-1 transition-colors duration-200 bg-orange-50 text-orange-700 border-orange-200 hover:bg-orange-100`}
                    >
                      <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M12 8v4m0 4h.01"/>
                        <circle cx="12" cy="12" r="10"/>
                      </svg>
                      Bugs
                      <span className={`min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-bold text-white ${hasNewBugs ? "bg-orange-500 animate-pulse" : "bg-orange-400"}`}>
                        {implicitBugs.length}
                      </span>
                    </button>
                    {showBugsDropdown && (
                      <div className="absolute top-full right-0 mt-1 w-80 bg-white border border-gray-200 rounded-xl shadow-lg z-50 max-h-80 overflow-y-auto">
                        <div className="px-3 py-2 border-b border-gray-100 text-xs font-semibold text-gray-500 sticky top-0 bg-white rounded-t-xl">Known Bugs ({implicitBugs.length})</div>
                        {implicitBugs.map((bug, i) => (
                          <div key={i} className="px-3 py-2.5 border-b border-gray-50 hover:bg-gray-50 transition-colors">
                            <div className="flex items-center gap-2">
                              {bug.url ? (
                                <a href={bug.url} target="_blank" rel="noreferrer" className="font-mono text-purple-700 font-semibold text-xs hover:underline">{bug.bug_id}</a>
                              ) : (
                                <span className="font-mono text-purple-700 font-semibold text-xs">{bug.bug_id}</span>
                              )}
                              {bug.status && <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-gray-100 text-gray-600">{bug.status}</span>}
                              {bug.priority && <span className="text-[10px] text-gray-400">{bug.priority}</span>}
                              <span className="ml-auto text-[10px] px-1.5 py-0.5 rounded-full bg-orange-100 text-orange-600 font-medium">{bug.match_pct}%</span>
                            </div>
                            {bug.summary && <p className="text-xs text-gray-600 line-clamp-2">{bug.summary}</p>}
                          </div>
                        ))}
                        <div className="px-3 py-2 text-center cursor-pointer hover:bg-orange-50 transition-colors rounded-b-xl" onClick={() => { if (loadingMoreBugs) return; setLoadingMoreBugs(true); socketRef.current?.emit("implicitSearch", { type: "bugs", top_k: implicitBugs.length + 5 }); }}>
                          {loadingMoreBugs ? <span className="text-[11px] text-orange-500 font-medium animate-pulse">Loading...</span> : <span className="text-[11px] text-orange-600 font-medium hover:underline">Load More</span>}
                        </div>
                      </div>
                    )}
                  </div>
                )}

                {implicitCases.length > 0 && (
                  <div className="relative">
                    <button
                      onClick={() => { setShowCasesDropdown(v => !v); setShowBugsDropdown(false); if (hasNewCases) setHasNewCases(false); }}
                      className={`px-2 py-1.5 border rounded-md text-xs font-medium flex items-center gap-1 transition-colors duration-200 bg-cyan-50 text-cyan-700 border-cyan-200 hover:bg-cyan-100`}
                    >
                      <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                        <path d="M9 12l2 2 4-4"/>
                        <rect x="3" y="3" width="18" height="18" rx="2"/>
                      </svg>
                      Cases
                      <span className={`min-w-[18px] h-[18px] flex items-center justify-center rounded-full text-[10px] font-bold text-white ${hasNewCases ? "bg-cyan-500 animate-pulse" : "bg-cyan-400"}`}>
                        {implicitCases.length}
                      </span>
                    </button>
                    {showCasesDropdown && (
                      <div className="absolute top-full right-0 mt-1 w-80 bg-white border border-gray-200 rounded-xl shadow-lg z-50 max-h-80 overflow-y-auto">
                        <div className="px-3 py-2 border-b border-gray-100 text-xs font-semibold text-gray-500 sticky top-0 bg-white rounded-t-xl">Past Cases ({implicitCases.length})</div>
                        {implicitCases.map((c, i) => (
                          <div key={i} className="px-3 py-2.5 border-b border-gray-50 hover:bg-gray-50 transition-colors">
                            <div className="flex items-center justify-between gap-2">
                              <span className="text-xs font-mono font-semibold text-cyan-700">{c.url ? <a href={c.url} target="_blank" rel="noreferrer" className="hover:underline">{c.case_number || c.case_id}</a> : (c.case_number || c.case_id)}</span>
                              <span className="text-[10px] px-1.5 py-0.5 rounded-full bg-cyan-100 text-cyan-600 font-medium">{c.match_pct}%</span>
                            </div>
                            <p className="text-xs text-gray-700 mt-0.5 line-clamp-2">{c.subject}</p>
                            {c.resolution && <p className="text-[10px] text-gray-400 mt-0.5 line-clamp-1">Resolution: {c.resolution}</p>}
                          </div>
                        ))}
                        <div className="px-3 py-2 text-center cursor-pointer hover:bg-cyan-50 transition-colors rounded-b-xl" onClick={() => { if (loadingMoreCases) return; setLoadingMoreCases(true); socketRef.current?.emit("implicitSearch", { type: "cases", top_k: implicitCases.length + 5 }); }}>
                          {loadingMoreCases ? <span className="text-[11px] text-cyan-500 font-medium animate-pulse">Loading...</span> : <span className="text-[11px] text-cyan-600 font-medium hover:underline">Load More</span>}
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>
              {feedbackBlockedNotice && (
                <div className="mt-2 text-xs text-amber-700 bg-amber-50 border border-amber-200 rounded-md px-2.5 py-1.5 inline-block">
                  {feedbackBlockedNotice}
                </div>
              )}
            </div>

            {/* Dashboard Messages */}
            <div 
              ref={dashboardChatBodyRef}
              className="flex-1 overflow-y-auto p-4 space-y-4"
              onClick={(e) => {
                const imageLink = e.target.closest('a.image-link');
                if (imageLink) {
                  e.preventDefault();
                  e.stopPropagation();
                  const imageUrl = imageLink.getAttribute('data-image-url');
                  if (imageUrl) {
                    handleImageClick(imageUrl);
                  }
                }
              }}
            >
              {dashboardMessages.length === 0 && !isThinking ? (
                <div className={`text-center ${currentTheme.textSecondary} mt-8`}>
                  <svg width="48" height="48" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24" className="mx-auto mb-4 text-gray-400">
                    <path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/>
                  </svg>
                  <p>No messages yet. Start the conversation!</p>
                </div>
              ) : (
                <>
                  {(() => { const _lastIdx = dashboardMessages.reduce((acc, m, i) => (m.sender === "copilot" || m.sender === "testpilot" || m.type === "info") ? i : acc, -1); latestCopilotMsgRef.current = null; return dashboardMessages.map((msg, index) => { const _isLatestCopilot = index === _lastIdx;
                  // Render session-clear divider
                  if (msg.type === "session_clear_divider") {
                    return (
                      <div
                        key={index}
                        ref={index === dashboardMessages.length - 1 ? clearDividerRef : null}
                        className="w-full"
                      >
                        <div className="flex items-center gap-3 py-3 px-4 mb-2 bg-teal-50 border border-teal-200 rounded-xl text-teal-700 text-sm font-medium">
                          <div>
                            <span className="font-semibold">Conversation cleared!</span>
                            <span className="font-normal text-teal-600 ml-1">How can I help you today? Ask your next question and I'll start fresh.</span>
                          </div>
                        </div>
                        {/* White space below divider so viewport feels fresh */}
                        <div style={{ height: "calc(100vh - 260px)" }} />
                      </div>
                    );
                  }


                  {/* ── TSE: Searching animation (shown while RAG is running) ── */}
                  if (msg.type === "rag_searching") {
                    const searchText = msg.tool === "search_past_cases" ? "Looking for Cases..." : msg.tool === "search_bugs_tool" ? "Looking for Bugs..." : "Searching...";
                    return (
                      <div key={index} className="w-full flex items-center gap-2 mb-2 px-1">
                        <div className="flex items-center gap-1">
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                          <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                        </div>
                        <span className="text-xs text-gray-400">{searchText}</span>
                      </div>
                    );
                  }

                  {/* ── TSE: RAG results (after search completes) ── */}
                  if (msg.type === "rag_results") {
                    // Hide completely - no need to show results indicator
                    return null;
                  }

                  if (msg.type === "answered_question" && msg.questionSnapshot) {
                    const { question, options = [] } = msg.questionSnapshot;

                    return (
                      <div key={index} className="w-full flex flex-col mb-4 items-start">
                        <div className="w-full rounded-[12px] border border-gray-150 bg-white px-6 py-2 shadow-sm">
                          <div
                            className="text-[13px] leading-6 text-gray-800"
                            style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                            dangerouslySetInnerHTML={{ __html: formatBotMessage(question) }}
                          />

                          {options.length > 0 && (
                            <div className="mt-1 flex flex-col gap-2">
                              {options.map((opt, optIndex) => {
                                const isSelected = opt === msg.selectedAnswer;

                                return (
                                  <div
                                    key={optIndex}
                                    className={`rounded-[10px] border px-4 py-0.5 text-[13px] leading-7 transition ${
                                      isSelected
                                        ? "border-[#06b6d4] bg-cyan-50 text-[#253439]"
                                        : "border-gray-200 bg-white text-[#253439]"
                                    }`}
                                    style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                                    dangerouslySetInnerHTML={{ __html: formatBotMessage(opt) }}
                                  />
                                );
                              })}
                            </div>
                          )}
                        </div>
                        <div className="mt-1 text-xs text-gray-400 font-medium flex items-center gap-1">
                          Copilot {formatTimestamp(msg.timestamp)}
                          {msg.questionSnapshot?.cache_hit && <CacheIndicator />}
                        </div>
                      </div>
                    );
                  }

                  {/* ── TSE: Info messages (context and reasoning) ── */}
                  if (msg.type === "info") {
                    return (
                      <div key={index} ref={_isLatestCopilot ? latestCopilotMsgRef : undefined} className="w-full flex flex-col mb-4 items-start">
                        <div className="px-5 py-3 rounded-2xl max-w-[85%] break-words border border-cyan-200 bg-cyan-50 text-cyan-900 shadow-sm"
                          style={{
                            fontFamily: "'Space Grotesk', sans-serif",
                            fontSize: "13px",
                            whiteSpace: "pre-line",
                          }}
                        >
                          <span dangerouslySetInnerHTML={{ __html: formatBotMessage(msg.displayText || msg.text) }} />
                        </div>
                        <div className="mt-1 text-xs text-gray-400 font-medium">
                          Copilot {formatTimestamp(msg.timestamp)}
                        </div>
                      </div>
                    );
                  }

                  {/* ── TSE: Feedback reminder — separate blob shown after session close ── */}
                  if (msg.type === "feedback_prompt") {
                    return (
                      <div key={index} className="w-full flex flex-col mb-4 items-start">
                        <div className="px-5 py-3 rounded-2xl max-w-[85%] break-words border border-violet-200 bg-violet-50 text-violet-900 shadow-sm flex items-center justify-between gap-3 flex-wrap"
                          style={{ fontFamily: "'Space Grotesk', sans-serif", fontSize: "13px" }}
                        >
                          <span className="font-semibold">{msg.text}</span>
                          <button
                            onClick={openFeedbackModal}
                            className="px-3 py-1 bg-violet-600 hover:bg-violet-700 text-white rounded-md text-xs font-medium whitespace-nowrap transition-colors"
                            type="button"
                          >
                            Open Feedback Form
                          </button>
                        </div>
                        <div className="mt-1 text-xs text-gray-400 font-medium">
                          Copilot {formatTimestamp(msg.timestamp)}
                        </div>
                      </div>
                    );
                  }

                  // Check if message should show action buttons
                  const shouldShowActions = msg.sender === "copilot" && 
                    msg.text && !msg.text.includes("Action triggered:") && 
                    !msg.text.includes("Determining");
                  const shouldShowMapAction = msg.sender === "copilot" && msg.text;
                  
                  return (
                    <div
                      key={index}
                      ref={_isLatestCopilot ? latestCopilotMsgRef : undefined}
                      className="w-full flex flex-col mb-4 items-start"
                    >
                      <div className="flex w-full items-start gap-3">
                        <div className="flex max-w-[85%] flex-col items-start">
                          <div
                            className={`px-5 py-4 rounded-2xl w-full break-words border shadow-sm ${
                              msg.sender === "agent"
                                ? "bg-[#06b6d4] text-white border-[#06b6d4]"
                                : msg.sender === "user"
                                  ? "bg-white text-[#253439] border-gray-200"
                                  : "bg-white text-cyan-900 border-cyan-300"
                            }`}
                            style={{
                              fontFamily: "'Space Grotesk', ui-sans-serif, system-ui, -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif",
                              fontSize: "14px"
                            }}
                          >
                            <div dangerouslySetInnerHTML={{ __html: formatBotMessage(msg.displayText || msg.text) }} />
                            {/* Usage tag for TSE assistant messages */}
                            {msg.usage && msg.sender === "copilot" && (
                              <div className="mt-2 text-xs text-gray-500 font-mono" style={{fontVariantNumeric: 'tabular-nums'}}>
                                ↳ in:{msg.usage.input_tokens || 0} out:{msg.usage.output_tokens || 0} cw:{msg.usage.cache_write_tokens || 0} cr:{msg.usage.cache_read_tokens || 0} · ${(msg.usage.cost_usd || 0).toFixed(4)} (saved ${(msg.usage.savings_usd || 0).toFixed(4)})
                              </div>
                            )}
                          </div>
                          <div className="mt-1 text-xs text-gray-400 font-medium">
                            {msg.sender === "agent"
                              ? `${agentName} ${formatTimestamp(msg.timestamp)}`
                              : msg.sender === "user"
                                ? `Customer ${formatTimestamp(msg.timestamp)}`
                                : `Copilot ${formatTimestamp(msg.timestamp)}`
                            }
                          </div>
                        </div>

                        {shouldShowMapAction && (
                          <button
                            onClick={() => openInlineDiagMapForNode(msg.diagMapNodeId, msg.diagMapTurn ?? null)}
                            className="ml-auto mt-2 flex h-8 w-8 shrink-0 items-center justify-center rounded-md text-gray-500 transition-colors duration-200 hover:bg-violet-50 hover:text-violet-700"
                            title="Show diagnostic map for this response"
                            type="button"
                          >
                            <svg width="16" height="16" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <circle cx="12" cy="5" r="3" /><circle cx="5" cy="19" r="3" /><circle cx="19" cy="19" r="3" />
                              <path d="M12 8v3M7.5 17.5L10.5 11M16.5 17.5L13.5 11" />
                            </svg>
                          </button>
                        )}
                      </div>
                      <div className="mt-1 text-xs text-gray-400 font-medium flex items-center gap-1" style={{maxWidth: '85%'}}>
                        {msg.sender === "agent"
                          ? `${agentName} ${formatTimestamp(msg.timestamp)}`
                          : msg.sender === "user"
                            ? `Customer ${formatTimestamp(msg.timestamp)}`
                            : `Copilot ${formatTimestamp(msg.timestamp)}`
                        }
                        {msg.cache_hit && <CacheIndicator />}
                      </div>
                      
                      {/* Action Icons - Only show for copilot messages that don't contain excluded text */}
                      {shouldShowActions && (
                        <div className="flex gap-2 mt-2 ml-2 relative">
                          {/* Copy Icon */}
                          <button
                            onClick={() => {
                              navigator.clipboard.writeText(msg.text);
                              // Optional: Show a brief toast notification
                              console.log("Message copied to clipboard");
                            }}
                            className="p-1.5 text-gray-500 hover:text-cyan-600 hover:bg-cyan-50 rounded-md transition-colors duration-200"
                            title="Copy message"
                          >
                            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <rect x="9" y="9" width="13" height="13" rx="2" ry="2"/>
                              <path d="M5 15H4a2 2 0 0 1-2-2V4a2 2 0 0 1 2-2h9a2 2 0 0 1 2 2v1"/>
                            </svg>
                          </button>
                          
                          {/* Send Icon */}
                          <button
                            onClick={() => {
                              // Send the exact message to customer chat
                              const newMessage = {
                                text: msg.text,
                                sender: "agent",
                                timestamp: Date.now()
                              };
                              setCustomerMessages(prev => [...prev, newMessage]);
                              
                              const sock = socketRef.current;
                              if (sock?.connected) {
                                const targetUserSid = currentSession?.user_sid || "all";
                                sock.emit("agentMessage", { 
                                  text: msg.text, 
                                  user_sid: targetUserSid,
                                  target: "customer"
                                });
                              }
                            }}
                            className="p-1.5 text-gray-500 hover:text-green-600 hover:bg-green-50 rounded-md transition-colors duration-200"
                            title="Send to customer"
                          >
                            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path d="M22 2 11 13"/>
                              <path d="M22 2l-7 20-4-9-9-4z"/>
                            </svg>
                          </button>
                          
                          {/* Edit Icon */}
                          <button
                            onClick={() => {
                              // Copy message to customer chat input for editing
                              setCustomerMessage(msg.text);
                              
                              // Use setTimeout to ensure state update happens first
                              setTimeout(() => {
                                const customerInput = document.querySelector('textarea[placeholder*="Ask follow-up"]');
                                if (customerInput) {
                                  // Auto-resize the textarea to fit content
                                  customerInput.style.height = 'auto';
                                  customerInput.style.height = Math.min(customerInput.scrollHeight, 200) + 'px';
                                  
                                  // Focus and set cursor at the end
                                  customerInput.focus();
                                  customerInput.setSelectionRange(customerInput.value.length, customerInput.value.length);
                                  
                                  // Scroll to top of the textarea if it has scrollbar
                                  if (customerInput.scrollHeight > customerInput.clientHeight) {
                                    customerInput.scrollTop = 0;
                                  }
                                }
                              }, 0);
                            }}
                            className="p-1.5 text-gray-500 hover:text-blue-600 hover:bg-blue-50 rounded-md transition-colors duration-200"
                            title="Edit in customer chat"
                          >
                            <svg width="14" height="14" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path d="M11 4H4a2 2 0 0 0-2 2v14a2 2 0 0 0 2 2h14a2 2 0 0 0 2-2v-7"/>
                              <path d="M18.5 2.5a2.121 2.121 0 0 1 3 3L12 15l-4 1 1-4 9.5-9.5z"/>
                            </svg>
                          </button>
                        </div>
                      )}
                      
                      {/* Join Session Button - Show for system messages with session data */}
                      {msg.sender === "system" && msg.sessionData && (
                        <div className="flex gap-2 mt-2 ml-2">
                          <button
                            onClick={() => handleJoinSession(msg.sessionData)}
                            className="px-3 py-1.5 bg-green-500 hover:bg-green-600 text-white border border-green-600 rounded-md text-xs font-medium transition-colors duration-200 flex items-center gap-1"
                            title="Join this user's session"
                            type="button"
                          >
                            <svg width="12" height="12" fill="none" stroke="currentColor" strokeWidth="2" viewBox="0 0 24 24">
                              <path d="M15 3h4a2 2 0 0 1 2 2v14a2 2 0 0 1-2 2h-4"/>
                              <polyline points="10,17 15,12 10,7"/>
                              <line x1="15" y1="12" x2="3" y2="12"/>
                            </svg>
                            Join Session
                          </button>
                        </div>
                      )}
                    </div>
                  );
                }); })()}

                {/* Thinking animation - shown while searching */}
                {isThinking && (
                  <div className="w-full flex items-center gap-2 mb-4 px-1">
                    <div className="flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                    <span className="text-xs text-gray-400">Copilot is thinking…</span>
                  </div>
                )}

                {/* Processing animation - shown after searches complete, waiting for response */}
                {isProcessing && (
                  <div className="w-full flex items-center gap-2 mb-4 px-1">
                    <div className="flex items-center gap-1">
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '0ms' }} />
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '150ms' }} />
                      <span className="w-1.5 h-1.5 bg-gray-400 rounded-full animate-bounce" style={{ animationDelay: '300ms' }} />
                    </div>
                    <span className="text-xs text-gray-400">Processing…</span>
                  </div>
                )}
                </>
              )}
            </div>

            {/* Dashboard Message Input */}
            <div className={`bg-white rounded-xl border shadow-md flex flex-col px-6 py-4 mx-4 mb-6 flex-shrink-0 ${
              currentQuestion ? 'border-[#06b6d4] border-2 ring-2 ring-cyan-100' : waitingForAnswer ? 'border-gray-300' : 'border-[#06b6d4]'
            }`}>
              {/* Show current question options above input if they exist */}
              {currentQuestion && (
                <div className="w-full mb-3 pb-3 border-b border-cyan-200 relative">
                  {/* Header with question text and action buttons */}
                  <div className="flex items-start justify-between gap-2 mb-2">
                    <div className="text-sm text-gray-600 flex-1" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                      <span dangerouslySetInnerHTML={{ __html: formatBotMessage(currentQuestion.question) }} />
                    </div>
                    {/* Action buttons in top right corner */}
                    <div className="flex items-center gap-1 flex-shrink-0">
                      {currentQuestion.cache_hit && <CacheIndicator />}
                      {/* Collapse button */}
                      <button
                        onClick={() => setIsQuestionCollapsed(!isQuestionCollapsed)}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition"
                        aria-label={isQuestionCollapsed ? "Expand question" : "Collapse question"}
                        title={isQuestionCollapsed ? "Expand question" : "Collapse question"}
                      >
                        <svg 
                          width="20" 
                          height="20" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          viewBox="0 0 24 24"
                          className={`transition-transform ${!isQuestionCollapsed ? 'rotate-180' : ''}`}
                        >
                          <path d="M5 15l7-7 7 7" />
                        </svg>
                      </button>
                      {/* Close button */}
                      <button
                        onClick={() => {
                          setCurrentQuestion(null);
                          setWaitingForAnswer(false);
                          setIsQuestionCollapsed(false);
                        }}
                        className="p-1.5 text-gray-400 hover:text-gray-600 hover:bg-gray-100 rounded transition"
                        aria-label="Close question"
                        title="Close question"
                      >
                        <svg 
                          width="20" 
                          height="20" 
                          fill="none" 
                          stroke="currentColor" 
                          strokeWidth="2" 
                          viewBox="0 0 24 24"
                        >
                          <path d="M18 6L6 18M6 6l12 12" />
                        </svg>
                      </button>
                    </div>
                  </div>
                  
                  {/* Collapsible content */}
                  {!isQuestionCollapsed && (
                    <div className="overflow-y-auto" style={{ maxHeight: "55vh" }}>
                      {/* Handle different input types */}
                      {currentQuestion.input_type === "text" || !currentQuestion.options?.length ? (
                        <TextQuestionWidget onSubmit={submitAnswer} />
                      ) : currentQuestion.input_type === "single" ? (
                        <>
                          <div className="flex flex-col gap-2">
                            {currentQuestion.options.map((opt, idx) => (
                              <button
                                key={idx}
                                onClick={() => submitAnswer(opt)}
                                className="text-left px-4 py-1.5 border border-gray-200 rounded-lg text-sm text-[#253439] hover:border-[#06b6d4] hover:bg-cyan-50 transition"
                                style={{ fontFamily: "'Space Grotesk', sans-serif" }}
                              >
                                <div dangerouslySetInnerHTML={{ __html: formatBotMessage(opt) }} />
                              </button>
                            ))}
                          </div>
                          <div className="text-xs text-gray-400 italic mt-2" style={{ fontFamily: "'Space Grotesk', sans-serif" }}>
                            {/* Select an option above, or type a clarifying question below */}
                          </div>
                        </>
                      ) : (
                        <MultiSelectWidget options={currentQuestion.options} onSubmit={submitAnswer} formatBotMessage={formatBotMessage} />
                      )}
                    </div>
                  )}
                </div>
              )}
              
              {dashboardSendError ? (
                <p className="text-sm text-red-600 mb-2 px-0.5" role="alert">
                  {dashboardSendError}
                </p>
              ) : null}
              {/* Always show input field */}
              <div className="flex items-start w-full">
                <textarea
                  className={`flex-1 bg-transparent border-none outline-none placeholder-[#A2A9AF] focus:ring-0 resize-none overflow-y-auto ${isThinking || isProcessing ? 'text-gray-400 cursor-not-allowed' : 'text-[#253439]'}`}
                  style={{
                    minHeight: '28px',
                    maxHeight: '200px',
                    height: '28px',
                    fontFamily: "'Space Grotesk', ui-sans-serif, system-ui, -apple-system, 'system-ui', 'Segoe UI', Roboto, 'Helvetica Neue', Arial, 'Noto Sans', sans-serif",
                    fontSize: "14px"
                  }}
                  placeholder={
                    isThinking || isProcessing
                      ? "Copilot is responding… click stop to interrupt"
                      : currentQuestion
                        ? "Select an option above or type a clarifying question..."
                        : "Ask follow-up, or use natural language like 'find bugs', 'search documentation'..."
                  }
                  value={dashboardMessage}
                  onChange={(e) => {
                    setDashboardMessage(e.target.value);
                    autoResizeTextarea(e.target);
                  }}
                  onKeyDown={handleDashboardKeyPress}
                  disabled={isThinking || isProcessing}
                />
                {isThinking || isProcessing ? (
                  <button
                    className="ml-2 bg-red-50 hover:bg-red-100 border border-red-200 rounded-lg p-2 transition"
                    aria-label="Stop answering"
                    title="Stop answering"
                    onClick={() => { socketRef.current?.emit("stopGeneration", {}); setIsThinking(false); setIsProcessing(false); }}
                    type="button"
                  >
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="#dc2626">
                      <rect x="6" y="6" width="12" height="12" rx="2" />
                    </svg>
                  </button>
                ) : (
                  <button
                    className="ml-2 bg-[#06b6d4] hover:bg-cyan-700 rounded-lg p-2 transition"
                    aria-label="Send"
                    onClick={sendDashboardMessage}
                    type="button"
                  >
                    <svg width="20" height="20" fill="none" stroke="white" strokeWidth="2" viewBox="0 0 24 24">
                      <path d="M22 2 11 13" />
                      <path d="M22 2l-7 20-4-9-9-4z" />
                    </svg>
                  </button>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Image Zoom Modal */}
      {zoomImageUrl && <ImagePreviewModal imageUrl={zoomImageUrl} onClose={closeZoom} />}
      {showFeedbackModal && (
        <FeedbackModal
          surface="engineer"
          sessionId={socketRef.current?.id}
          username={agentName}
          firstPrompt={firstPromptRef.current}
          onClose={() => setShowFeedbackModal(false)}
        />
      )}
      {/* Mini inline — cumulative growing snapshot, fades nodes beyond maxTurn */}
      <DiagnosticMapWindow
        open={diagMapInlineOpen && !diagMapFullOpen}
        minimized={diagMapInlineMinimized}
        inline
        onOpen={() => setDiagMapInlineMinimized(false)}
        onMinimize={() => setDiagMapInlineMinimized(true)}
        onClose={() => { setDiagMapInlineOpen(false); setDiagMapInlineMinimized(false); }}
        trail={diagMapTrail}
        snapshot={diagMapSnapshot}
        fullSnapshot={diagMapFullSnapshot}
        focusNodeId={diagMapFocusNodeId}
        focusKey={diagMapFocusKey}
        maxTurn={diagMapMaxTurn}
        title="Diagnostic Map"
        isGraphLoading={graphLoading}
      />
      {/* Full screen — own snapshot + own focus, isolated from mini */}
      <DiagnosticMapWindow
        open={diagMapFullOpen}
        minimized={diagMapFullMinimized}
        onOpen={openDiagMapFullWindow}
        onMinimize={() => setDiagMapFullMinimized(true)}
        onClose={() => { setDiagMapFullOpen(false); setDiagMapFullMinimized(false); }}
        trail={diagMapTrail}
        snapshot={diagMapFullSnapshot ?? diagMapSnapshot}
        focusNodeId={diagMapFullFocusNodeId}
        focusKey={diagMapFullFocusKey}
        title="Diagnostic Map"
        isGraphLoading={graphLoading}
      />
    </div>
  );
};

export default TSECopilotDashboard; 

